// Admin — Stripe Webhook Health
// Pings GET /api/admin/stripe/webhook-health and displays whether the LIVE
// webhook is correctly attached to our PUBLIC_BASE_URL with all required events.
import { useEffect, useState } from "react";
import { Loader2, CheckCircle2, XCircle, AlertTriangle, ExternalLink, RefreshCw, Copy } from "lucide-react";
import { adminApi } from "@/lib/adminApi";

const AdminStripeWebhookPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await adminApi.stripeWebhookHealth();
      setData(d);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await adminApi.stripeWebhookHealth();
        if (!cancelled) { setData(d); setError(null); }
      } catch (e) {
        if (!cancelled) setError(e.message || String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const copy = (txt) => {
    try { navigator.clipboard?.writeText(txt); } catch { /* noop */ }
  };

  if (loading) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center" data-testid="webhook-loading">
        <Loader2 className="animate-spin text-[color:var(--gv-pink)]" size={24} />
      </div>
    );
  }
  if (error) {
    return (
      <div className="max-w-2xl mx-auto bg-red-50 border border-red-200 text-red-700 p-4 rounded-[5px]" data-testid="webhook-error">
        {error}
      </div>
    );
  }
  if (!data) return null;

  const expectedUrl = data.expected_url || "(PUBLIC_BASE_URL non configurée)";
  const isLive = data.stripe_mode === "live";

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6" data-testid="admin-stripe-webhook">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 mb-1">Admin · Production health</div>
          <h1 className="font-display text-3xl text-[color:var(--gv-ink)]">Stripe Webhook</h1>
        </div>
        <button onClick={load} className="inline-flex items-center gap-2 px-4 py-2 rounded-[5px] border border-[color:var(--gv-line)] text-[13px] hover:border-[color:var(--gv-pink)] hover:text-[color:var(--gv-pink)] transition-colors" data-testid="webhook-refresh">
          <RefreshCw size={14} /> Rafraîchir
        </button>
      </div>

      {/* Overall health */}
      <div className={`p-5 rounded-[5px] border ${data.healthy ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`} data-testid="webhook-health-banner">
        <div className="flex items-start gap-3">
          {data.healthy
            ? <CheckCircle2 size={22} className="text-emerald-600 shrink-0 mt-0.5" />
            : <AlertTriangle size={22} className="text-amber-600 shrink-0 mt-0.5" />}
          <div className="flex-1">
            <div className="font-display text-[18px] text-[color:var(--gv-ink)] mb-1">
              {data.healthy ? "Webhook LIVE opérationnel ✓" : "Action requise — webhook non opérationnel"}
            </div>
            <div className="text-[13px] text-[color:var(--gv-ink)]/75">
              Stripe en mode <strong>{isLive ? "LIVE" : "TEST"}</strong> · {data.endpoints_count} endpoint(s) configuré(s) sur le compte.
              {data.healthy && data.healthy_endpoint_id && <> · ID actif : <code className="text-[12px] bg-white px-1.5 py-0.5 rounded">{data.healthy_endpoint_id}</code></>}
            </div>
          </div>
        </div>
      </div>

      {/* Expected URL block */}
      <div className="bg-white border border-[color:var(--gv-line)] rounded-[5px] p-5">
        <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/55 mb-2">URL attendue (production)</div>
        <div className="flex items-center gap-3 mb-3">
          <code className="flex-1 bg-[color:var(--gv-mist)] px-3 py-2 rounded-[5px] text-[13px] text-[color:var(--gv-ink)] break-all" data-testid="webhook-expected-url">{expectedUrl}</code>
          <button onClick={() => copy(expectedUrl)} className="px-3 py-2 rounded-[5px] border border-[color:var(--gv-line)] hover:border-[color:var(--gv-pink)] text-[12px] inline-flex items-center gap-1.5">
            <Copy size={13} /> Copier
          </button>
        </div>
        {!data.configured_url && (
          <div className="text-[12.5px] text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2 rounded-[5px]">
            ⚠️ <code>PUBLIC_BASE_URL</code> non configurée côté backend — impossible de vérifier la correspondance d&apos;URL.
          </div>
        )}
      </div>

      {/* List of endpoints */}
      <div className="space-y-3" data-testid="webhook-endpoints-list">
        {data.endpoints.map((ep) => {
          const ok = ep.matches_expected_url && ep.has_required_events && ep.status === "enabled" && ep.livemode === true;
          return (
            <div key={ep.id} className={`bg-white border rounded-[5px] p-4 ${ok ? "border-emerald-200" : ep.matches_expected_url ? "border-amber-200" : "border-[color:var(--gv-line)]"}`} data-testid={`webhook-ep-${ep.id}`}>
              <div className="flex items-start gap-3 mb-2">
                {ok
                  ? <CheckCircle2 size={18} className="text-emerald-600 shrink-0 mt-0.5" />
                  : ep.matches_expected_url
                    ? <AlertTriangle size={18} className="text-amber-600 shrink-0 mt-0.5" />
                    : <XCircle size={18} className="text-[color:var(--gv-ink)]/30 shrink-0 mt-0.5" />}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <code className="text-[12.5px] text-[color:var(--gv-ink)] break-all">{ep.url}</code>
                    <a href={`https://dashboard.stripe.com/${ep.livemode ? "" : "test/"}webhooks/${ep.id}`} target="_blank" rel="noreferrer" className="text-[color:var(--gv-pink)] hover:opacity-70" title="Open in Stripe Dashboard">
                      <ExternalLink size={13} />
                    </a>
                  </div>
                  <div className="text-[11.5px] text-[color:var(--gv-ink)]/60 mt-1 flex items-center gap-2 flex-wrap">
                    <span>{ep.id}</span>
                    <span>·</span>
                    <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase tracking-widest font-medium ${ep.livemode ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{ep.livemode ? "LIVE" : "TEST"}</span>
                    <span>·</span>
                    <span className={ep.status === "enabled" ? "text-emerald-600" : "text-red-600"}>{ep.status}</span>
                    <span>·</span>
                    <span>{ep.enabled_events_count} événements</span>
                    {ep.api_version && (<><span>·</span><span>API {ep.api_version}</span></>)}
                  </div>
                </div>
              </div>
              {ep.missing_required_events.length > 0 && (
                <div className="mt-2 ml-7 text-[12px] text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2 rounded-[5px]" data-testid={`webhook-ep-${ep.id}-missing`}>
                  <strong>Événements manquants :</strong>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {ep.missing_required_events.map((ev) => (
                      <code key={ev} className="bg-white px-1.5 py-0.5 rounded text-[11px]">{ev}</code>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {data.endpoints.length === 0 && (
          <div className="bg-amber-50 border border-amber-200 text-amber-800 p-5 rounded-[5px] text-center" data-testid="webhook-empty">
            Aucun webhook configuré sur ce compte Stripe.
          </div>
        )}
      </div>

      {/* Required events reference */}
      <details className="bg-white border border-[color:var(--gv-line)] rounded-[5px] p-4">
        <summary className="cursor-pointer text-[13.5px] font-medium text-[color:var(--gv-ink)]">Voir les événements requis ({data.required_events.length})</summary>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
          {data.required_events.map((ev) => (
            <code key={ev} className="bg-[color:var(--gv-mist)] px-2 py-1 rounded text-[11.5px] text-[color:var(--gv-ink)]">{ev}</code>
          ))}
        </div>
      </details>
    </div>
  );
};

export default AdminStripeWebhookPage;
